/**
 * The error envelope.
 *
 * Every failure the API returns has this shape, for a reason that is about the interface rather
 * than about tidiness: ux.md requires errors to appear inline next to the field, in plain
 * language, rather than as a summary at the top after submission. That is only possible if the
 * server says *which field*, in a form the client can attach to a control without parsing prose.
 *
 * `message` is the plain-language sentence. `code` is what the client switches on. `fields` is
 * what the form renders next to each input.
 */

import { z } from "zod";

export const ERROR_CODES = [
  "validation",
  "unauthenticated",
  "forbidden",
  "not_found",
  /** A version check failed — someone else changed this while you were working. */
  "conflict",
  /** The action is legal but the current state forbids it: scoring a locked category, paying a
   *  rule whose results are not yet approved. Distinct from `forbidden`, which is about who you
   *  are rather than about when it is. */
  "precondition_failed",
  "rate_limited",
  "unavailable",
  "internal",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const fieldErrorSchema = z.object({
  /** Dotted path into the submitted body, e.g. `entries.0.categoryId`. */
  path: z.string(),
  message: z.string(),
});

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    fields: z.array(fieldErrorSchema).optional(),
    /** Correlates a user-visible failure with the log line that explains it. */
    requestId: z.string().optional(),
    /** Present on `conflict`, so a client can show what changed rather than only that it did. */
    currentVersion: z.number().int().optional(),
  }),
});

export type ApiError = z.infer<typeof apiErrorSchema>;
export type FieldError = z.infer<typeof fieldErrorSchema>;

/** The HTTP status each code maps to. One table, so a route cannot pick a different one. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  validation: 422,
  unauthenticated: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  precondition_failed: 412,
  rate_limited: 429,
  unavailable: 503,
  internal: 500,
};

/**
 * Flatten a Zod failure into field errors.
 *
 * Zod's own `flatten` loses nesting, which is exactly what a registration form needs — an error
 * on the third entry's category has to land on the third entry's control, not on the form.
 */
export function toFieldErrors(error: z.ZodError): FieldError[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join("."),
    message: issue.message,
  }));
}
